import {createContext, use, useMemo, useState, type Dispatch, type SetStateAction} from 'react'
import type {LogEntry, Session} from '../types/session'
import type {PublicUser} from '../types/public-user'

interface SessionContextType {
  session: Session
  sessionOwner: PublicUser | undefined
  logs?: LogEntry[]
  logsAreLoading?: boolean
  mcpToolNameMappings: Record<string, string>
  setMcpToolNameMappings: Dispatch<SetStateAction<Record<string, string>>>
  taskSharingStatus?: 'unshared' | 'shared'
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined)

export function useSessionContext() {
  const context = use(SessionContext)
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionContextProvider')
  }
  return context
}

interface SessionContextProviderProps {
  session: Session
  sessionOwner: PublicUser | undefined
  logs?: LogEntry[]
  logsAreLoading?: boolean
  taskSharingStatus?: 'unshared' | 'shared'
  children: React.ReactNode
}

export function SessionContextProvider({
  session,
  sessionOwner,
  logs,
  logsAreLoading,
  taskSharingStatus,
  children,
}: SessionContextProviderProps) {
  const [mcpToolNameMappings, setMcpToolNameMappings] = useState<Record<string, string>>({})
  const value = useMemo(
    () => ({
      session,
      sessionOwner,
      logs,
      logsAreLoading,
      mcpToolNameMappings,
      setMcpToolNameMappings,
      taskSharingStatus,
    }),
    [session, sessionOwner, logs, logsAreLoading, mcpToolNameMappings, taskSharingStatus],
  )
  return <SessionContext value={value}>{children}</SessionContext>
}
